/**
 * Binary Format Types for Frontend
 *
 * Frontend-specific types for the Binary Format Editor UI.
 * Mirrors backend types without Mongoose-specific dependencies.
 */

// ================================
// Core Types
// ================================

/** Input format for binary data */
export type InputFormat = 'base64' | 'hex' | 'binary' | 'raw';

/** Byte order (endianness) */
export type Endianness = 'BE' | 'LE';

/** Supported data types for binary field extraction */
export type DataType =
  | 'uint8'    // Unsigned 8-bit integer (0-255)
  | 'int8'     // Signed 8-bit integer (-128 to 127)
  | 'uint16'   // Unsigned 16-bit integer (0-65535)
  | 'int16'    // Signed 16-bit integer (-32768 to 32767)
  | 'uint32'   // Unsigned 32-bit integer
  | 'int32'    // Signed 32-bit integer
  | 'uint64'   // Unsigned 64-bit integer (BigInt)
  | 'int64'    // Signed 64-bit integer (BigInt)
  | 'float32'  // IEEE 754 single precision float
  | 'float64'  // IEEE 754 double precision float
  | 'bool'     // Boolean (0 = false, non-zero = true)
  | 'bits'     // Extract N bits at bit offset
  | 'string'   // ASCII string with length
  | 'utf8'     // UTF-8 string with length
  | 'hex'      // Raw bytes as hex string
  | 'bytes';   // Raw bytes as number array

/** Validation mode for parsing */
export type ValidationMode = 'strict' | 'lenient' | 'silent';

/** Format lifecycle status */
export type FormatStatus = 'draft' | 'active' | 'deprecated';

/** Transform type for value post-processing */
export type TransformType = 'scale' | 'offset' | 'expression' | 'lookup' | 'round' | 'mask';

// ================================
// Field & Transform Definitions
// ================================

/** Transform definition for post-extraction value processing */
export interface Transform {
  type: TransformType;
  /**
   * Transform value:
   * - scale: number to multiply by
   * - offset: number to add
   * - expression: string expression using $value placeholder
   * - lookup: object mapping numeric values to strings
   * - round: number of decimal places
   * - mask: bitmask to apply (AND operation)
   */
  value: number | string | Record<number, string>;
}

/** Field validation constraints */
export interface FieldValidation {
  min?: number;
  max?: number;
  required?: boolean;
}

/** Binary field definition for extraction */
export interface BinaryField {
  id: string;
  name: string;
  description?: string;
  offset: number;
  bitOffset?: number;
  bitLength?: number;
  type: DataType;
  length?: number;
  endian?: Endianness;
  transforms?: Transform[];
  validation?: FieldValidation;
  defaultValue?: unknown;
}

/** Sample payload for testing format definitions */
export interface SamplePayload {
  name: string;
  data: string;
  expectedOutput?: Record<string, unknown>;
}

// ================================
// Binary Format Document
// ================================

/** Binary format definition */
export interface BinaryFormat {
  _id: string;
  name: string;
  slug: string;
  version: number;
  description?: string;
  tags?: string[];
  inputFormat: InputFormat;
  defaultEndian: Endianness;
  expectedBytes?: number;
  validationMode: ValidationMode;
  fields: BinaryField[];
  samplePayloads?: SamplePayload[];
  status: FormatStatus;
  isSystemTemplate: boolean;
  owner_type: 'User' | 'Organization';
  owner_id: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Inline format definition (for node config) */
export interface InlineBinaryFormat {
  inputFormat: InputFormat;
  defaultEndian: Endianness;
  expectedBytes?: number;
  validationMode?: ValidationMode;
  fields: BinaryField[];
}

// ================================
// Parse Results
// ================================

/** Parse error details */
export interface ParseError {
  field: string;
  message: string;
  offset?: number;
  code: 'BUFFER_UNDERRUN' | 'INVALID_TYPE' | 'VALIDATION_FAILED' | 'TRANSFORM_FAILED' | 'DECODE_FAILED';
}

/** Result of parsing binary data */
export interface ParseResult {
  success: boolean;
  data: Record<string, unknown>;
  errors: ParseError[];
  warnings: string[];
  bytesProcessed: number;
  executionTimeMs: number;
}

/** Response for test parsing */
export interface TestParseResponse {
  result: ParseResult;
  hexDump: string;
  decimalDump: number[];
  byteCount: number;
}

// ================================
// API DTOs
// ================================

/** Request to create a new binary format */
export interface CreateBinaryFormatDTO {
  name: string;
  description?: string;
  tags?: string[];
  inputFormat: InputFormat;
  defaultEndian: Endianness;
  expectedBytes?: number;
  validationMode?: ValidationMode;
  fields: BinaryField[];
  samplePayloads?: SamplePayload[];
}

/** Request to update a binary format */
export interface UpdateBinaryFormatDTO {
  name?: string;
  description?: string;
  tags?: string[];
  inputFormat?: InputFormat;
  defaultEndian?: Endianness;
  expectedBytes?: number;
  validationMode?: ValidationMode;
  fields?: BinaryField[];
  samplePayloads?: SamplePayload[];
}

/** Filters for listing binary formats */
export interface BinaryFormatListFilters {
  status?: FormatStatus | FormatStatus[];
  tags?: string[];
  search?: string;
  includeSystemTemplates?: boolean;
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

/** Paginated list response */
export interface PaginatedBinaryFormats {
  items: BinaryFormat[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// ================================
// UI Helper Types
// ================================

/** Data type metadata for UI display */
export interface DataTypeInfo {
  type: DataType;
  label: string;
  bytes: number | 'variable';
  requiresLength: boolean;
  supportsBitFields: boolean;
  description: string;
}

/** Available data types with metadata */
export const DATA_TYPE_INFO: DataTypeInfo[] = [
  { type: 'uint8', label: 'Unsigned 8-bit', bytes: 1, requiresLength: false, supportsBitFields: false, description: '0 to 255' },
  { type: 'int8', label: 'Signed 8-bit', bytes: 1, requiresLength: false, supportsBitFields: false, description: '-128 to 127' },
  { type: 'uint16', label: 'Unsigned 16-bit', bytes: 2, requiresLength: false, supportsBitFields: false, description: '0 to 65,535' },
  { type: 'int16', label: 'Signed 16-bit', bytes: 2, requiresLength: false, supportsBitFields: false, description: '-32,768 to 32,767' },
  { type: 'uint32', label: 'Unsigned 32-bit', bytes: 4, requiresLength: false, supportsBitFields: false, description: '0 to 4,294,967,295' },
  { type: 'int32', label: 'Signed 32-bit', bytes: 4, requiresLength: false, supportsBitFields: false, description: '-2B to 2B' },
  { type: 'uint64', label: 'Unsigned 64-bit', bytes: 8, requiresLength: false, supportsBitFields: false, description: 'BigInt' },
  { type: 'int64', label: 'Signed 64-bit', bytes: 8, requiresLength: false, supportsBitFields: false, description: 'BigInt' },
  { type: 'float32', label: 'Float (32-bit)', bytes: 4, requiresLength: false, supportsBitFields: false, description: 'IEEE 754 single' },
  { type: 'float64', label: 'Float (64-bit)', bytes: 8, requiresLength: false, supportsBitFields: false, description: 'IEEE 754 double' },
  { type: 'bool', label: 'Boolean', bytes: 1, requiresLength: false, supportsBitFields: false, description: '0=false, else=true' },
  { type: 'bits', label: 'Bit Field', bytes: 'variable', requiresLength: false, supportsBitFields: true, description: 'Extract specific bits' },
  { type: 'string', label: 'ASCII String', bytes: 'variable', requiresLength: true, supportsBitFields: false, description: 'Fixed-length ASCII' },
  { type: 'utf8', label: 'UTF-8 String', bytes: 'variable', requiresLength: true, supportsBitFields: false, description: 'Fixed-length UTF-8' },
  { type: 'hex', label: 'Hex Bytes', bytes: 'variable', requiresLength: true, supportsBitFields: false, description: 'Raw bytes as hex string' },
  { type: 'bytes', label: 'Byte Array', bytes: 'variable', requiresLength: true, supportsBitFields: false, description: 'Raw bytes as number[]' },
];

/** Transform type metadata for UI */
export interface TransformTypeInfo {
  type: TransformType;
  label: string;
  valueType: 'number' | 'string' | 'lookup';
  description: string;
  placeholder: string;
}

/** Available transform types with metadata */
export const TRANSFORM_TYPE_INFO: TransformTypeInfo[] = [
  { type: 'scale', label: 'Scale', valueType: 'number', description: 'Multiply by factor', placeholder: '0.1' },
  { type: 'offset', label: 'Offset', valueType: 'number', description: 'Add/subtract value', placeholder: '-40' },
  { type: 'round', label: 'Round', valueType: 'number', description: 'Round to N decimal places', placeholder: '2' },
  { type: 'mask', label: 'Bit Mask', valueType: 'number', description: 'Apply AND bitmask', placeholder: '0xFF' },
  { type: 'expression', label: 'Expression', valueType: 'string', description: 'Custom formula using $value', placeholder: '$value * 2 + 10' },
  { type: 'lookup', label: 'Lookup Table', valueType: 'lookup', description: 'Map values to strings', placeholder: '0=Off, 1=On' },
];

/** Get byte size for a data type */
export function getDataTypeBytes(type: DataType): number | null {
  const info = DATA_TYPE_INFO.find(d => d.type === type);
  if (!info || info.bytes === 'variable') return null;
  return info.bytes;
}

/** Check if data type requires length property */
export function dataTypeRequiresLength(type: DataType): boolean {
  return ['string', 'utf8', 'hex', 'bytes'].includes(type);
}

/** Check if data type is multi-byte (needs endianness) */
export function dataTypeIsMultiByte(type: DataType): boolean {
  return ['uint16', 'int16', 'uint32', 'int32', 'uint64', 'int64', 'float32', 'float64'].includes(type);
}

/** Generate a unique field ID */
export function generateFieldId(): string {
  return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/** Create a new empty field */
export function createEmptyField(offset: number = 0): BinaryField {
  return {
    id: generateFieldId(),
    name: '',
    offset,
    type: 'uint8',
    transforms: [],
  };
}

/** Create a new empty format */
export function createEmptyFormat(): Omit<BinaryFormat, '_id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'owner_type' | 'owner_id'> {
  return {
    name: '',
    slug: '',
    version: 1,
    description: '',
    tags: [],
    inputFormat: 'base64',
    defaultEndian: 'BE',
    validationMode: 'lenient',
    fields: [],
    samplePayloads: [],
    status: 'draft',
    isSystemTemplate: false,
  };
}
